import { configById, type MeldContext, type RuleConfig } from '@rummy/engine';
import type { SeatView, SessionView } from '@rummy/protocol';
import { useMemo, useState } from 'react';
import { useI18n } from '../i18n/index.tsx';
import { HandResultList } from './HandResultList.tsx';
import { PlayingCard } from './PlayingCard.tsx';

export interface HandHistoryProps {
  session: SessionView;
  seats: readonly SeatView[];
  accountId: string;
  /** The live table's rules, used only for hands recorded before `configId`
   *  was kept. A session's seat count is fixed at its start, so this is the
   *  same config those hands were dealt under. */
  fallbackRules: RuleConfig;
  onClose: () => void;
}

/**
 * Every hand played in this session, with the cards, reachable at any time.
 *
 * The summary that shows a finished hand auto-advances after twelve seconds,
 * which is the correct behaviour for a table waiting on the next deal and the
 * wrong one for the question people actually ask afterwards — *what did they
 * have?* The data was already on the client; nothing was showing it once the
 * sheet closed.
 *
 * Most recent hand first, and that one open: "the hand that just went" is what
 * is being asked about nearly every time.
 */
export function HandHistory({
  session,
  seats,
  accountId,
  fallbackRules,
  onClose,
}: HandHistoryProps) {
  const { t } = useI18n();
  const hands = useMemo(
    () => [...session.history].sort((a, b) => b.ordinal - a.ordinal),
    [session.history],
  );
  const [open, setOpen] = useState<number | null>(hands[0]?.ordinal ?? null);

  const handleFor = (player: string) =>
    seats.find((s) => s.id === player)?.handle ?? player;

  return (
    <div className="sheet sheet--history" role="dialog" aria-modal="true">
      <div className="sheet__panel">
        <header className="sheet__head">
          <h2>{t('history.heading')}</h2>
          <button type="button" className="btn btn--ghost" onClick={onClose}>
            {t('history.close')}
          </button>
        </header>

        {hands.length === 0 ? (
          <p className="muted">{t('history.empty')}</p>
        ) : (
          <ol className="history">
            {hands.map((hand) => {
              const expanded = open === hand.ordinal;
              // Each hand is read under its own wild rank and rules. Reusing the
              // live table's would silently restate which cards were jokers.
              const ctx: MeldContext = {
                config: configById(hand.configId) ?? fallbackRules,
                wildRank: hand.wildRank ?? null,
              };
              const winner = hand.results.find((r) => r.outcome === 'won');
              return (
                <li key={hand.ordinal} className="history__hand">
                  <button
                    type="button"
                    className="history__toggle"
                    aria-expanded={expanded}
                    onClick={() => setOpen(expanded ? null : hand.ordinal)}
                  >
                    <span className="history__ordinal">
                      {t('history.hand', { n: hand.ordinal + 1 })}
                    </span>
                    {hand.wildCard && (
                      <span className="history__wild">
                        <PlayingCard id={hand.wildCard} wild />
                      </span>
                    )}
                    <span className="history__winner">
                      {winner
                        ? t('history.wonBy', { name: handleFor(winner.player) })
                        : t('history.noWinner')}
                    </span>
                  </button>

                  {expanded && (
                    <HandResultList
                      results={hand.results}
                      reveal={hand.reveal}
                      handleFor={handleFor}
                      accountId={accountId}
                      ctx={ctx}
                    />
                  )}
                </li>
              );
            })}
          </ol>
        )}
      </div>
    </div>
  );
}
